import {
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { MUIDataTableColumn } from 'mui-datatables';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CustomDataTable from '../../../../components/CustomDataTable';
import TableSkeleton from '../../../../components/TableSkeleton';
import OccurrencyDTO from '../../../../models/dto/OccurrencyDTO';
import { BaseEnum } from '../../../../models/enum/BaseEnum';
import OCCURENCY_CHIP_SEARCH_ENUM, {
  AFASTADO_PONTO,
  ATESTADO_PONTO,
  FALTA_S_JUSTIFICATIVA,
  FERIAS,
  FERIAS_PONTO,
  getAbsenceDescriptionByFilter,
  HORAS_EXTRAS,
} from '../../../../models/enum/OccurencyChipSearchEnum';
import {
  EXTRAHOUR_OCCURRENCY_ENUM,
  FALTA_SEM_JUSTIFICATIVA,
  VACATIONHOURS_OCCURRENCY_ENUM,
} from '../../../../models/enum/OccurrencyEnum';
import { getCancelTokenSource, isCancel } from '../../../../services/api';
import {
  findAbsences,
  findOccurrences,
} from '../../../../services/personnelDepartment.service';
import { AppState } from '../../../../store';
import { CondominiumReducerData } from '../../../../store/reducers/condominiumReducer';
import {
  subtract,
  toDefaultServerDateFormat,
} from '../../../../utils/date.util';
import { isNullOrEmpty } from '../../../../utils/validation.util';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chip: {
      margin: '5px 0 5px 10px',
    },
    root: {
      height: '100%',
    },
    cardHeaderContainer: {
      padding: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: 0,
    },
    cardSubHeaderContainer: {
      padding: theme.spacing(2),
      paddingTop: 0,
    },
    cardContentContainer: {
      height: '100%',
      overflowY: 'auto',
    },
    gridTableChips: {
      margin: '6px',
      alignItems: 'center',
    },
    chipInsideTable: {
      marginRight: '10px',
      '&&:focus': {
        backgroundColor: theme.palette.text.disabled,
      },
    },
  }),
);

const OccurrencesCard: React.FC = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [occurrences, setOccurrences] = useState<OccurrencyDTO[]>([]);
  const [occurrencesFiltered, setOccurrencesFiltered] = useState<
    OccurrencyDTO[]
  >([]);
  const { condominiumSelected }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const chooseChipSearchFunction = useCallback(
    async (type: BaseEnum) => {
      let occurrencyCodes: number[] = [];
      let abscenceDescriptions: string[] = [];
      setOccurrencesFiltered([]);
      switch (type) {
        case FALTA_S_JUSTIFICATIVA:
          occurrencyCodes = [FALTA_SEM_JUSTIFICATIVA.numberValue];
          break;
        case HORAS_EXTRAS: {
          occurrencyCodes = EXTRAHOUR_OCCURRENCY_ENUM.map(
            item => item.numberValue,
          );
          break;
        }
        case FERIAS: {
          occurrencyCodes = VACATIONHOURS_OCCURRENCY_ENUM.map(
            item => item.numberValue,
          );
          break;
        }
        case FERIAS_PONTO:
        case AFASTADO_PONTO:
        case ATESTADO_PONTO:
          abscenceDescriptions = getAbsenceDescriptionByFilter(type);
          break;
        default:
          setOccurrencesFiltered(occurrences);
          break;
      }

      if (!isNullOrEmpty(occurrencyCodes)) {
        const filtered = occurrences.filter(item =>
          occurrencyCodes.some(code => item.codigo === code),
        );
        setOccurrencesFiltered(filtered);
      } else if (!isNullOrEmpty(abscenceDescriptions)) {
        const { data: absences } = await findAbsences();
        const filtered = absences.filter(item =>
          abscenceDescriptions.some(code => item.nomeOcorrencia === code),
        );
        setOccurrencesFiltered(filtered);
      }
    },
    [occurrences],
  );

  const chooseChipSearch = useCallback(
    async (type: BaseEnum) => {
      try {
        setLoadingFilter(true);
        await chooseChipSearchFunction(type);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingFilter(false);
      }
    },
    [chooseChipSearchFunction],
  );

  useEffect(() => {
    const cancelTokenSource = getCancelTokenSource();
    const findAllOccurrences = async () => {
      try {
        setLoading(true);
        const referencia = condominiumSelected?.referencia;
        if (referencia) {
          const currentDate = new Date();
          const sevenDaysAgo = toDefaultServerDateFormat(
            //limite de dias definido internamente na api
            subtract(currentDate, 65, 'days'),
          );
          const finalDate = toDefaultServerDateFormat(currentDate);
          const { data } = await findOccurrences(
            sevenDaysAgo,
            finalDate,
            referencia,
            cancelTokenSource.token,
          );
          setOccurrences(data);
          setOccurrencesFiltered(data);
        }
      } catch (error) {
        if (!isCancel(error)) {
          console.log(error);
        }
      } finally {
        setLoading(false);
      }
    };

    findAllOccurrences();
    return () => {
      cancelTokenSource.cancel();
    };
  }, [condominiumSelected]);

  const columns: MUIDataTableColumn[] = [
    {
      name: 'nomeFuncionario',
      label: 'Nome do Colaborador',
    },
    {
      name: 'cargo',
      label: 'Função',
    },
    {
      name: 'dia',
      label: 'Data',
    },
    {
      name: 'marcacao',
      label: 'Marcação',
    },
    {
      name: 'nomeOcorrencia',
      label: 'Ocorrência',
    },
  ];

  return (
    <Card style={{ height: '100%' }}>
      <Grid container className={classes.cardHeaderContainer}>
        <Typography color="textSecondary" variant="h5">
          Detalhe das Ocorrências
          {loadingFilter && (
            <CircularProgress
              size={24}
              style={{ marginLeft: 15, position: 'relative', top: 4 }}
            />
          )}
        </Typography>
      </Grid>
      <CardContent className={classes.cardContentContainer}>
        {loading ? (
          <TableSkeleton rows={3} />
        ) : (
          <>
            <CustomDataTable
              data={occurrencesFiltered?.sort((a, b) =>
                a?.nomeFuncionario > b?.nomeFuncionario ? 1 : -1,
              )}
              columns={columns}
              options={{
                rowsPerPage: 10,
                elevation: 0,
                print: true,
                download: true,
              }}
              title={
                <>
                  <Grid
                    container
                    className={classes.gridTableChips}
                    justify="flex-start"
                  >
                    {OCCURENCY_CHIP_SEARCH_ENUM.map(
                      (item: BaseEnum, index: number) => {
                        return (
                          <Chip
                            key={index}
                            label={item.label}
                            className={classes.chipInsideTable}
                            disabled={loading || loadingFilter}
                            variant="outlined"
                            clickable
                            onClick={() => chooseChipSearch(item)}
                          />
                        );
                      },
                    )}
                  </Grid>
                </>
              }
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OccurrencesCard;
